/* eslint-disable no-nested-ternary */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'wouter';

import * as AppPropTypes from '../../../lib/PropTypes';

import { useContest } from '../../../contexts/ContestContext';
import Blocks from '../../blocks/Blocks';
import Button from '../../buttons/Button';
import StyledButton from '../../buttons/StyledButton';
import GameItem from '../../items/Game';
import HelpModal from '../../modals/HelpModal';
import UserBanner from '../../partials/UserBanner';

import styles from '../../../../styles/themes/defis/play.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    canPlay: PropTypes.bool,
};

const defaultProps = {
    canPlay: true,
};

function PlayPage({ page, canPlay }) {
    const intl = useIntl();
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const { ended = false } = useContest() || {};
    const user = useUser();
    const { unusedPincode = null } = user || {};
    const { code = null } = unusedPincode || {};
    const { stats = {} } = user || {};
    const { score = null } = stats || {};
    const { games: initialGames = [], blocks = null } = page;

    const games = useMemo(() => sortBy(initialGames || [], 'id'), [initialGames]);

    const [game, setGame] = useState(null);
    const closeModal = useCallback(() => {
        setGame(null);
    }, [setGame]);
    const onClickGame = useCallback(
        (newGame) => {
            setGame(newGame);
        },
        [setGame],
    );

    const confirmGame = useCallback(() => {
        navigate(route('game', { game: game.id }));
    }, [game, navigate, route]);

    return (
        <div className={styles.container}>
            {user !== null ? (
                <UserBanner
                    user={user}
                    points={score || null}
                    prefix={
                        <FormattedMessage
                            defaultMessage="Total score:"
                            description="Banner label"
                        />
                    }
                />
            ) : null}
            <div className={styles.center}>
                {canPlay && games.length > 0 ? (
                    <div className={styles.bonusContainer}>
                        <h2 className={styles.title}>
                            <FormattedMessage
                                defaultMessage="Choose your challenge"
                                description="Play page title"
                            />
                        </h2>
                        {games.map((g) => (
                            <div className={styles.bonusGameContainer}>
                                <GameItem
                                    className={classNames([
                                        styles.bonusGame,
                                        {
                                            [styles.disabled]: code === null,
                                        },
                                    ])}
                                    game={g}
                                    onClick={() => onClickGame(g)}
                                    withOrder
                                    titlePrefix={intl.formatMessage({
                                        defaultMessage: 'Challenge',
                                        description: 'Card title',
                                    })}
                                    small
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.noGamesContainer}>
                        <div className={styles.noGames}>
                            <h1 className={styles.title}>
                                {ended ? (
                                    <FormattedMessage
                                        defaultMessage="The contest has ended. Come back next time!"
                                        description="Play page title"
                                    />
                                ) : null}
                                {games === null || games.length === 0 ? (
                                    <FormattedMessage
                                        defaultMessage="No games today. Come back tomorrow!"
                                        description="Play page title"
                                    />
                                ) : null}
                            </h1>
                            <Button href={route('profile')} className={styles.button}>
                                <FormattedMessage
                                    defaultMessage="Go to your account"
                                    description="Button label"
                                />
                            </Button>
                        </div>
                    </div>
                )}
                {blocks !== null && blocks.length > 0 ? (
                    <Blocks
                        isContainer
                        className={styles.blocks}
                        containerClassName={styles.blocksInner}
                        blocks={blocks}
                    />
                ) : null}
            </div>

            {game !== null ? (
                <HelpModal
                    onClose={closeModal}
                    withCloseButton
                    contentClassName={styles.helpContent}
                    closeButtonClassName={styles.modalCloseButton}
                >
                    <div className={styles.modal}>
                        <h3 className={styles.modalTitle}>
                            <FormattedMessage
                                defaultMessage="Confirm your selection"
                                description="Modal title"
                            />
                        </h3>
                        <GameItem className={styles.modalGame} game={game} />
                        <div className={styles.modalButtons}>
                            <StyledButton onClick={closeModal} className={styles.modalButton}>
                                <FormattedMessage
                                    defaultMessage="Cancel"
                                    description="Modal title"
                                />
                            </StyledButton>
                            <StyledButton onClick={confirmGame} className={styles.modalButton}>
                                <FormattedMessage
                                    defaultMessage="Confirm"
                                    description="Modal title"
                                />
                            </StyledButton>
                        </div>
                    </div>
                </HelpModal>
            ) : null}
        </div>
    );
}

PlayPage.propTypes = propTypes;
PlayPage.defaultProps = defaultProps;

export default PlayPage;
